import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiInfo, FiMusic, FiTarget, FiTrendingUp } = FiIcons;

const InfoPanel = ({ selectedKey }) => {
  const keyData = {
    'C': {
      major: 'C Major',
      minor: 'A Minor',
      sharps: 0,
      flats: 0,
      signature: 'No sharps or flats',
      scale: ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
      chords: ['C', 'Dm', 'Em', 'F', 'G', 'Am', 'B°'],
      relative: 'A Minor',
      parallel: 'C Minor',
      description: 'The most fundamental key in Western music, with no sharps or flats.'
    },
    'G': {
      major: 'G Major',
      minor: 'E Minor',
      sharps: 1,
      flats: 0,
      signature: '1 sharp (F♯)',
      scale: ['G', 'A', 'B', 'C', 'D', 'E', 'F♯'],
      chords: ['G', 'Am', 'Bm', 'C', 'D', 'Em', 'F♯°'],
      relative: 'E Minor',
      parallel: 'G Minor',
      description: 'A bright, uplifting key commonly used in folk and popular music.'
    },
    'D': {
      major: 'D Major',
      minor: 'B Minor',
      sharps: 2,
      flats: 0,
      signature: '2 sharps (F♯, C♯)',
      scale: ['D', 'E', 'F♯', 'G', 'A', 'B', 'C♯'],
      chords: ['D', 'Em', 'F♯m', 'G', 'A', 'Bm', 'C♯°'],
      relative: 'B Minor',
      parallel: 'D Minor',
      description: 'A brilliant key that works well for string instruments and triumphant themes.'
    },
    'A': {
      major: 'A Major',
      minor: 'F♯ Minor',
      sharps: 3,
      flats: 0,
      signature: '3 sharps (F♯, C♯, G♯)',
      scale: ['A', 'B', 'C♯', 'D', 'E', 'F♯', 'G♯'],
      chords: ['A', 'Bm', 'C♯m', 'D', 'E', 'F♯m', 'G♯°'],
      relative: 'F♯ Minor',
      parallel: 'A Minor',
      description: 'A warm, resonant key favored by guitarists and classical composers.'
    },
    'E': {
      major: 'E Major',
      minor: 'C♯ Minor',
      sharps: 4,
      flats: 0,
      signature: '4 sharps (F♯, C♯, G♯, D♯)',
      scale: ['E', 'F♯', 'G♯', 'A', 'B', 'C♯', 'D♯'],
      chords: ['E', 'F♯m', 'G♯m', 'A', 'B', 'C♯m', 'D♯°'],
      relative: 'C♯ Minor',
      parallel: 'E Minor',
      description: 'A powerful key with strong resonance, popular in rock and blues.'
    },
    'B': {
      major: 'B Major',
      minor: 'G♯ Minor',
      sharps: 5,
      flats: 0,
      signature: '5 sharps (F♯, C♯, G♯, D♯, A♯)',
      scale: ['B', 'C♯', 'D♯', 'E', 'F♯', 'G♯', 'A♯'],
      chords: ['B', 'C♯m', 'D♯m', 'E', 'F♯', 'G♯m', 'A♯°'],
      relative: 'G♯ Minor',
      parallel: 'B Minor',
      description: 'A brilliant, sharp key often used for virtuosic passages and bright orchestrations.'
    },
    'G♭': {
      major: 'G♭ Major',
      minor: 'E♭ Minor',
      sharps: 0,
      flats: 6,
      signature: '6 flats (B♭, E♭, A♭, D♭, G♭, C♭)',
      scale: ['G♭', 'A♭', 'B♭', 'C♭', 'D♭', 'E♭', 'F'],
      chords: ['G♭', 'A♭m', 'B♭m', 'C♭', 'D♭', 'E♭m', 'F°'],
      relative: 'E♭ Minor',
      parallel: 'G♭ Minor',
      description: 'The enharmonic equivalent of F♯ Major, rarely used but theoretically important.'
    },
    'D♭': {
      major: 'D♭ Major',
      minor: 'B♭ Minor',
      sharps: 0,
      flats: 5,
      signature: '5 flats (B♭, E♭, A♭, D♭, G♭)',
      scale: ['D♭', 'E♭', 'F', 'G♭', 'A♭', 'B♭', 'C'],
      chords: ['D♭', 'E♭m', 'Fm', 'G♭', 'A♭', 'B♭m', 'C°'],
      relative: 'B♭ Minor',
      parallel: 'D♭ Minor',
      description: 'A rich, warm key favored by jazz musicians and romantic composers.'
    },
    'A♭': {
      major: 'A♭ Major',
      minor: 'F Minor',
      sharps: 0,
      flats: 4,
      signature: '4 flats (B♭, E♭, A♭, D♭)',
      scale: ['A♭', 'B♭', 'C', 'D♭', 'E♭', 'F', 'G'],
      chords: ['A♭', 'B♭m', 'Cm', 'D♭', 'E♭', 'Fm', 'G°'],
      relative: 'F Minor',
      parallel: 'A♭ Minor',
      description: 'A mellow, lyrical key often used for expressive melodies and ballads.'
    },
    'E♭': {
      major: 'E♭ Major',
      minor: 'C Minor',
      sharps: 0,
      flats: 3,
      signature: '3 flats (B♭, E♭, A♭)',
      scale: ['E♭', 'F', 'G', 'A♭', 'B♭', 'C', 'D'],
      chords: ['E♭', 'Fm', 'Gm', 'A♭', 'B♭', 'Cm', 'D°'],
      relative: 'C Minor',
      parallel: 'E♭ Minor',
      description: 'A noble, heroic key favored for symphonic works and brass instruments.'
    },
    'B♭': {
      major: 'B♭ Major',
      minor: 'G Minor',
      sharps: 0,
      flats: 2,
      signature: '2 flats (B♭, E♭)',
      scale: ['B♭', 'C', 'D', 'E♭', 'F', 'G', 'A'],
      chords: ['B♭', 'Cm', 'Dm', 'E♭', 'F', 'Gm', 'A°'],
      relative: 'G Minor',
      parallel: 'B♭ Minor',
      description: 'A comfortable key for wind instruments and vocal music, warm and accessible.'
    },
    'F': {
      major: 'F Major',
      minor: 'D Minor',
      sharps: 0,
      flats: 1,
      signature: '1 flat (B♭)',
      scale: ['F', 'G', 'A', 'B♭', 'C', 'D', 'E'],
      chords: ['F', 'Gm', 'Am', 'B♭', 'C', 'Dm', 'E°'],
      relative: 'D Minor',
      parallel: 'F Minor',
      description: 'A gentle, pastoral key often associated with nature and simplicity.'
    },
    // Minor keys
    'Am': {
      major: 'C Major',
      minor: 'A Minor',
      sharps: 0,
      flats: 0,
      signature: 'No sharps or flats',
      scale: ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
      chords: ['Am', 'B°', 'C', 'Dm', 'Em', 'F', 'G'],
      relative: 'C Major',
      parallel: 'A Major',
      description: 'The natural minor key, melancholic and expressive, widely used across all genres.'
    },
    'Em': {
      major: 'G Major',
      minor: 'E Minor',
      sharps: 1,
      flats: 0,
      signature: '1 sharp (F♯)',
      scale: ['E', 'F♯', 'G', 'A', 'B', 'C', 'D'],
      chords: ['Em', 'F♯°', 'G', 'Am', 'Bm', 'C', 'D'],
      relative: 'G Major',
      parallel: 'E Major',
      description: 'A contemplative key popular in folk and classical music, easy for guitarists.'
    },
    'Bm': {
      major: 'D Major',
      minor: 'B Minor',
      sharps: 2,
      flats: 0,
      signature: '2 sharps (F♯, C♯)',
      scale: ['B', 'C♯', 'D', 'E', 'F♯', 'G', 'A'],
      chords: ['Bm', 'C♯°', 'D', 'Em', 'F♯m', 'G', 'A'],
      relative: 'D Major',
      parallel: 'B Major',
      description: 'A dramatic minor key with strong emotional impact, favored in romantic music.'
    },
    'F♯m': {
      major: 'A Major',
      minor: 'F♯ Minor',
      sharps: 3,
      flats: 0,
      signature: '3 sharps (F♯, C♯, G♯)',
      scale: ['F♯', 'G♯', 'A', 'B', 'C♯', 'D', 'E'],
      chords: ['F♯m', 'G♯°', 'A', 'Bm', 'C♯m', 'D', 'E'],
      relative: 'A Major',
      parallel: 'F♯ Major',
      description: 'An intense, passionate key often used for dramatic and expressive passages.'
    },
    'C♯m': {
      major: 'E Major',
      minor: 'C♯ Minor',
      sharps: 4,
      flats: 0,
      signature: '4 sharps (F♯, C♯, G♯, D♯)',
      scale: ['C♯', 'D♯', 'E', 'F♯', 'G♯', 'A', 'B'],
      chords: ['C♯m', 'D♯°', 'E', 'F♯m', 'G♯m', 'A', 'B'],
      relative: 'E Major',
      parallel: 'C♯ Major',
      description: 'A deeply emotional key associated with longing and introspection.'
    },
    'G♯m': {
      major: 'B Major',
      minor: 'G♯ Minor',
      sharps: 5,
      flats: 0,
      signature: '5 sharps (F♯, C♯, G♯, D♯, A♯)',
      scale: ['G♯', 'A♯', 'B', 'C♯', 'D♯', 'E', 'F♯'],
      chords: ['G♯m', 'A♯°', 'B', 'C♯m', 'D♯m', 'E', 'F♯'],
      relative: 'B Major',
      parallel: 'G♯ Major',
      description: 'A rare, exotic key with a mysterious and ethereal character.'
    },
    'E♭m': {
      major: 'G♭ Major',
      minor: 'E♭ Minor',
      sharps: 0,
      flats: 6,
      signature: '6 flats (B♭, E♭, A♭, D♭, G♭, C♭)',
      scale: ['E♭', 'F', 'G♭', 'A♭', 'B♭', 'C♭', 'D♭'],
      chords: ['E♭m', 'F°', 'G♭', 'A♭m', 'B♭m', 'C♭', 'D♭'],
      relative: 'G♭ Major',
      parallel: 'E♭ Major',
      description: 'The enharmonic minor key, theoretical in nature and rarely used in practice.'
    },
    'B♭m': {
      major: 'D♭ Major',
      minor: 'B♭ Minor',
      sharps: 0,
      flats: 5,
      signature: '5 flats (B♭, E♭, A♭, D♭, G♭)',
      scale: ['B♭', 'C', 'D♭', 'E♭', 'F', 'G♭', 'A♭'],
      chords: ['B♭m', 'C°', 'D♭', 'E♭m', 'Fm', 'G♭', 'A♭'],
      relative: 'D♭ Major',
      parallel: 'B♭ Major',
      description: 'A dark, brooding key often used for tragic and melancholic themes.'
    },
    'Fm': {
      major: 'A♭ Major',
      minor: 'F Minor',
      sharps: 0,
      flats: 4,
      signature: '4 flats (B♭, E♭, A♭, D♭)',
      scale: ['F', 'G', 'A♭', 'B♭', 'C', 'D♭', 'E♭'],
      chords: ['Fm', 'G°', 'A♭', 'B♭m', 'Cm', 'D♭', 'E♭'],
      relative: 'A♭ Major',
      parallel: 'F Major',
      description: 'A somber, introspective key associated with deep emotion and pathos.'
    },
    'Cm': {
      major: 'E♭ Major',
      minor: 'C Minor',
      sharps: 0,
      flats: 3,
      signature: '3 flats (B♭, E♭, A♭)',
      scale: ['C', 'D', 'E♭', 'F', 'G', 'A♭', 'B♭'],
      chords: ['Cm', 'D°', 'E♭', 'Fm', 'Gm', 'A♭', 'B♭'],
      relative: 'E♭ Major',
      parallel: 'C Major',
      description: 'A dramatic, heroic minor key often used for powerful and emotional passages.'
    },
    'Gm': {
      major: 'B♭ Major',
      minor: 'G Minor',
      sharps: 0,
      flats: 2,
      signature: '2 flats (B♭, E♭)',
      scale: ['G', 'A', 'B♭', 'C', 'D', 'E♭', 'F'],
      chords: ['Gm', 'A°', 'B♭', 'Cm', 'Dm', 'E♭', 'F'],
      relative: 'B♭ Major',
      parallel: 'G Major',
      description: 'A noble, expressive minor key favored by Mozart and other classical masters.'
    },
    'Dm': {
      major: 'F Major',
      minor: 'D Minor',
      sharps: 0,
      flats: 1,
      signature: '1 flat (B♭)',
      scale: ['D', 'E', 'F', 'G', 'A', 'B♭', 'C'],
      chords: ['Dm', 'E°', 'F', 'Gm', 'Am', 'B♭', 'C'],
      relative: 'F Major',
      parallel: 'D Major',
      description: 'The saddest key according to many composers, deeply melancholic and moving.'
    }
  };

  const currentKey = keyData[selectedKey];

  return (
    <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg">
          <SafeIcon icon={FiInfo} className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white">Key Information</h2>
      </div>

      <AnimatePresence mode="wait">
        {currentKey ? (
          <motion.div
            key={selectedKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
              <h3 className="text-xl font-semibold text-white mb-2">
                {selectedKey.includes('m') && !selectedKey.includes('♯') && !selectedKey.includes('♭') ? currentKey.minor : currentKey.major}
              </h3>
              <p className="text-gray-300 text-sm">{currentKey.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiMusic} className="w-4 h-4 text-purple-400" />
                  <h4 className="font-semibold text-white">Key Signature</h4>
                </div>
                <p className="text-gray-300 text-sm">{currentKey.signature}</p>
              </div>

              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiTarget} className="w-4 h-4 text-pink-400" />
                  <h4 className="font-semibold text-white">Relative Key</h4>
                </div>
                <p className="text-gray-300 text-sm">{currentKey.relative}</p>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-indigo-400" />
                <h4 className="font-semibold text-white">Scale Notes</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentKey.scale.map((note, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gradient-to-r from-indigo-500/30 to-purple-500/30 rounded-full text-white text-sm font-medium border border-indigo-400/30"
                  >
                    {note}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <SafeIcon icon={FiMusic} className="w-4 h-4 text-cyan-400" />
                <h4 className="font-semibold text-white">Chord Progression</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentKey.chords.map((chord, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-full text-white text-sm font-medium border border-cyan-400/30"
                  >
                    {chord}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiMusic} className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Select a Key</h3>
            <p className="text-gray-400 text-sm max-w-xs mx-auto">
              Click or hover over any segment in the circle to view detailed information about that key.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InfoPanel;